import {
  Box,
  Grid,
  InputBase,
  Typography
} from "@mui/material";
import { motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ctaImage2 from "../../../assets/backgroundImage.jpg";
import ctaImage1 from "../../../assets/cta-pattern.png";
import muzukuruLogo from "../../../assets/muzukurublack.png";
import { showErrorToast } from "../../../components/common/ShowToast";
import apiService, { BaseURL } from "../../../service/Api";
import { RootState } from "../../../store/Index";
import {
  clearTwoFaUsername,
  login,
  User,
} from "../../../store/slice/AuthSlice";
import {
  clearTheme,
  IThemeState,
  setTheme,
} from "../../../store/slice/ThemeSlice";
import { fetchAssignedMemoryBooks } from "../../stream-management/components/memorial-page/comments-compiler/service/memoryBook.api";
import CtaSlideshow from "./CTASlideshow";

const Verify2FA = () => {
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const ctaImages = [ctaImage1, ctaImage2];
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const twoFaUsername = useSelector(
    (state: RootState) => state.auth.twoFaUsername,
  );

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const muzukuruLogoToUse = reduxTheme.logo || muzukuruLogo;

  const isStaging =
    BaseURL?.includes("staging.muzukuru.com") || BaseURL?.includes("staging");

  const handleChange = (value: string, index: number) => {
    if (isNaN(Number(value))) return;

    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const data = e.clipboardData.getData("text").trim();
    if (!/^\d{6}$/.test(data)) return;

    const pasteData = data.split("");
    setOtp(pasteData);

    inputRefs.current[5]?.focus();
  };

  const verifyCode = useCallback(async (fullCode: string) => {
    if (isLoading) return;
    if (!twoFaUsername) {
      showErrorToast(
        "No username found. Please log in again.",
        "2fa-username-error",
      );
      navigate("/login");
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiService.post<{
        access_token: string;
        refresh_token: string;
        user: User;
        is_2fa_enabled: boolean;
        message: string;
      }>("/api/authentication/2fa/login-verify/", {
        username: twoFaUsername,
        totp_code: fullCode,
      });

      dispatch(
        login({
          user: response.data.user,
          access_token: response.data.access_token,
          refresh_token: response.data.refresh_token,
          is_2fa_enabled: response.data.is_2fa_enabled,
        }),
      );

      localStorage.setItem("access_token", response.data.access_token);
      localStorage.setItem("refresh_token", response.data.refresh_token);
      dispatch(clearTwoFaUsername());
      dispatch(clearTheme());

      try {
        const themeResponse = await apiService.get<IThemeState>(
          `/api/companies/theme/${encodeURIComponent(response.data.user.username)}`,
        );
        dispatch(setTheme(themeResponse.data));
      } catch (themeErr) {
        console.error("Failed to fetch theme:", themeErr);
      }

      if (!response.data.user?.id) {
        console.error("User ID not found in response");
        navigate("/setup-2fa");
        return;
      }

      try {
        const assignmentsResponse = await fetchAssignedMemoryBooks(
          response.data.user.id,
        );
        const results = assignmentsResponse.data?.results ?? [];

        const hasActiveAssignments = results.some((assignment) => {
          const physicalComments = assignment.physical_comments || 0;
          const doneComments = assignment.done_comments || 0;
          return physicalComments > 0 && doneComments < physicalComments;
        });

        if (hasActiveAssignments) {
          navigate("/memory-book-dashboard", { state: { showModal: true } });
        } else {
          navigate("/setup-2fa");
        }
      } catch (err) {
        console.error("Failed to check assignments:", err);
        navigate("/setup-2fa");
      }
    } catch (err: unknown) {
      const errorInfo = err as {
        message?: string;
        response?: { data?: { error?: string }; status?: number };
      };
      let errorMessage = "Invalid 2FA code. Please try again.";
      if (errorInfo.response?.data?.error) {
        errorMessage = errorInfo.response.data.error;
      }
      showErrorToast(errorMessage, "2fa-verify-error");
      setOtp(new Array(6).fill(""));
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } finally {
      setIsLoading(false);
    }
  }, [twoFaUsername, dispatch, navigate, isLoading]) ;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const fullCode = otp.join("");

    if (fullCode.length !== 6) {
      showErrorToast("Please enter the full 6-digit code.", "2fa-code-error");
      return;
    }

    verifyCode(fullCode);
  };

  useEffect(() => {
    const fullCode = otp.join("");
    if (otp.every((d) => d !== "") && !isLoading) {
      const timeout = setTimeout(() => verifyCode(fullCode), 200);
      return () => clearTimeout(timeout);
    }
  }, [otp, isLoading, verifyCode]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.5, staggerChildren: 0.1 },
    },
  };
  const itemVariants = {
    hidden: { opacity: 0, y: 18 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
  };

  return (
    <Box
      sx={{
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        backgroundColor: "#ffffff",
        position: "relative",
        border: isStaging ? "10px solid #FFBB00" : "none",
        boxSizing: "border-box",
      }}
    >
      <Grid container sx={{ height: "100%" }}>
        {/* LEFT */}
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{
            height: "100%",
            backgroundColor: "#fff",
            position: "relative",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            px: { xs: 3, sm: 5, md: 7 },
            py: { xs: 3, md: 4 },
          }}
        >
          {isStaging && (
            <Box
              sx={{
                position: "absolute",
                left: -34,
                top: "50%",
                transform: "translateY(-50%) rotate(-90deg)",
                transformOrigin: "left center",
                backgroundColor: "#FFBB00",
                color: "#fff",
                fontWeight: 700,
                fontSize: "11px",
                letterSpacing: "2px",
                textTransform: "uppercase",
                px: 1.5,
                py: 0.6,
                borderRadius: "0 0 6px 6px",
                zIndex: 1000,
              }}
            >
              STAGING
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: { xs: 3, md: 5 },
            }}
          >
            <Box
              component="img"
              src={muzukuruLogoToUse}
              alt="Logo"
              sx={{ height: 48, objectFit: "contain" }}
            />
            <Box
              sx={{
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#6b4c3b"
                strokeWidth="1.5"
              >
                <circle cx="10" cy="8" r="3" />
                <path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
                <circle cx="17" cy="11" r="1.5" />
                <path d="M21 14.5c0-1.5-1-2.5-2.5-2.5" />
              </svg>
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              style={{ maxWidth: 420, width: "100%" }}
            >
              <Box component="form" onSubmit={handleSubmit}>
                <motion.div variants={itemVariants}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: "#1a1a1a",
                      mb: 0.5,
                      fontSize: { xs: "24px", md: "28px" },
                    }}
                  >
                    Verify Sign In
                  </Typography>
                </motion.div>

                <motion.div variants={itemVariants}>
                  <Typography
                    sx={{
                      color: "#6b6b6b",
                      fontSize: "15px",
                      mb: 4,
                      lineHeight: 1.6,
                    }}
                  >
                    Enter your 2FA from your Authenticator App
                  </Typography>
                </motion.div>

                {/* 6-Digit OTP Inputs */}
                <motion.div variants={itemVariants}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: { xs: 1, sm: 1.5 },
                      mb: 4,
                      justifyContent: "space-between",
                    }}
                  >
                    {otp.map((digit, index) => (
                      <InputBase
                        key={index}
                        inputRef={(el) => (inputRefs.current[index] = el)}
                        value={digit}
                        onChange={(e) => handleChange(e.target.value, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        onPaste={index === 0 ? handlePaste : undefined}
                        disabled={isLoading}
                        inputProps={{
                          inputMode: "numeric",
                          maxLength: 1,
                          style: {
                            textAlign: "center",
                            fontSize: "20px",
                            fontWeight: "700",
                            color: "#1a1a1a",
                          },
                        }}
                        sx={{
                          width: { xs: "45px", sm: "52px" },
                          height: "60px",
                          border: "1.5px solid",
                          borderColor: otp[index] ? "#6b4c3b" : "#d4d4d4",
                          borderRadius: "8px",
                          backgroundColor: "#fff",
                          "&:focus-within": {
                            borderColor: "#6b4c3b",
                            boxShadow: "0 0 0 1px #6b4c3b",
                          },
                        }}
                      />
                    ))}
                  </Box>
                </motion.div>

                {/* <motion.div variants={itemVariants}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={isLoading || otp.some((d) => d === "")}
                    sx={{
                      backgroundColor: "#6b4c3b",
                      textTransform: "none",
                      padding: "14px",
                      borderRadius: "8px",
                      fontSize: "16px",
                      fontWeight: 600,
                      boxShadow: "none",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#55392d",
                        boxShadow: "none",
                      },
                      "&:disabled": {
                        backgroundColor: "rgba(107,76,59,0.4)",
                        color: "#fff",
                      },
                    }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} sx={{ color: "#fff" }} />
                    ) : (
                      "Proceed"
                    )}
                  </Button>
                </motion.div> */}
              </Box>
            </motion.div>
          </Box>
        </Grid>

        <Grid
          size={{ xs: 0, md: 6 }}
          sx={{
            height: "100%",
            display: { xs: "none", md: "flex" },
            overflow: "hidden",
            backgroundColor: "#fff",
            p: 2,
            boxSizing: "border-box",
          }}
        >
          <CtaSlideshow images={ctaImages} intervalMs={5000} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Verify2FA;
